import { AlertTriangle, Loader2 } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'

export interface DeleteNoteDialogProps {
	open: boolean
	onClose: () => void
	onConfirm: () => void
	/** How many photographs go with it, so the warning is specific. */
	imageCount?: number
	busy?: boolean
	/** The server's refusal, when it refused. */
	error?: string | null
}

/**
 * The confirmation for deleting a note.
 *
 * Shared by the field app and the desktop Notes workspace. It lived under
 * `features/Field/components/` while the field app was its only caller (MYD-431)
 * and moved here when the desktop feeds got the same affordance (MYD-564), so
 * that `components/` never has to reach into a feature folder.
 *
 * **Deliberately heavy.** Deleting a note is the most destructive thing a note
 * surface offers: it is irreversible from the UI and it takes the note's
 * photographs with it. A dialog that says "Are you sure?" and offers a red
 * button does not convey that, so this one says what will happen, counts the
 * pictures, and puts Cancel first.
 *
 * Cancel is the default action and the wider target. Somebody who opened this
 * by accident should be able to leave without reading carefully; somebody who
 * meant it has to cross the room to the other button.
 */
export function DeleteNoteDialog({
	open,
	onClose,
	onConfirm,
	imageCount = 0,
	busy = false,
	error = null,
}: DeleteNoteDialogProps) {
	return (
		<Dialog open={open} onClose={busy ? () => {} : onClose}>
			<DialogContent className="max-w-sm">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
						Delete this note?
					</DialogTitle>
					<DialogDescription>
						This removes the note completely and <strong>cannot be undone</strong>.
						{imageCount > 0 && (
							<>
								{' '}
								The {imageCount === 1 ? 'photograph' : `${imageCount} photographs`} on it{' '}
								{imageCount === 1 ? 'is' : 'are'} removed from the gallery too.
							</>
						)}
					</DialogDescription>
				</DialogHeader>

				{error && (
					<p role="alert" className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
						{error}
					</p>
				)}

				{/* Cancel first and wider: the safe action should be the easy one. */}
				<div className="flex gap-2 pt-4">
					<Button variant="ghost" isInput onClick={onClose} disabled={busy} className="flex-1">
						Cancel
					</Button>
					<Button variant="danger" isInput onClick={onConfirm} disabled={busy}>
						{busy ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Deleting…
							</>
						) : (
							'Delete'
						)}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default DeleteNoteDialog
